import { useCallback, useEffect, useMemo, useRef } from "react";
import { useStickToBottom } from "use-stick-to-bottom";
import type { AssistantTranscriptNarration, AssistantTranscriptTimelineItem } from "../types";

export function getClaudiaTranscriptAutoFollowFingerprint(
  items: AssistantTranscriptTimelineItem[]
): string | null {
  const latestItem = items.at(-1);
  if (!latestItem) {
    return null;
  }

  if (latestItem.kind === "message") {
    return `message:${latestItem.key}`;
  }

  if (latestItem.kind === "activity_turn") {
    const latestTurnItem = latestItem.items.at(-1);
    const latestItemFingerprint = latestTurnItem
      ? `${latestTurnItem.id}:${latestTurnItem.status}:${latestTurnItem.text}`
      : latestItem.summaryText;
    return `activity-turn:${latestItem.key}:${latestItem.status}:${latestItemFingerprint}`;
  }

  return `${latestItem.kind}:${latestItem.key}`;
}

export interface ClaudiaTranscriptAutoFollow {
  /**
   * Goes on the `ScrollShadow` viewport. Already composed with the scroll-back ref that was
   * passed in, so the transcript wires exactly one ref into that node.
   */
  viewportRef: (node: HTMLDivElement | null) => void;
  /** Goes on the column that holds the transcript rows — the thing whose height grows. */
  contentRef: (node: HTMLDivElement | null) => void;
  /** False once the reader has scrolled away from the end; drives the scroll-to-bottom button. */
  isAtBottom: boolean;
  /** Re-engages the bottom lock and animates down. */
  scrollToBottom: () => void;
}

/**
 * Follow the end of the Claudia transcript — but only while the reader is still there (LAT-963).
 *
 * This used to call `scrollIntoView` on a bottom sentinel every time the content fingerprint
 * changed, which during streaming is several times a second: a reader scrolling up to re-read
 * something was dragged back to the bottom on the next token. `use-stick-to-bottom` owns the
 * hard part — it holds a bottom lock, and releases it the moment the user scrolls or wheels
 * upwards (it also treats a text selection that reaches into the viewport as an escape). While
 * the lock is held, its `ResizeObserver` on `contentRef` keeps the view pinned as content grows.
 *
 * Two things are kept from the old hook. The **fingerprint** still nudges the scroll, because a
 * transcript change is not always a *resize*: a status flip or a same-height narration swap moves
 * no pixels and would otherwise leave the last line just under the fold. And the nudge is gated on
 * the library's live lock (`state.isAtBottom`), not on the rendered `isAtBottom`, which is
 * `isAtBottom || isNearBottom` — a reader who scrolled up only 40px has escaped the lock but is
 * still "near" the bottom, and scrolling them down would undo exactly the escape this fixes.
 *
 * Scroll-back (`use-claudia-transcript-scroll-back.ts`) reads and writes `scrollTop` on the same
 * node, so its ref is composed in here rather than being wired separately: one ref, one node.
 * A prepend never fights the lock — the fingerprint is taken from the *last* item, so older pages
 * do not trigger a follow, and the library's own resize handler carries `preserveScrollPosition`,
 * so it stays put unless the lock is genuinely held.
 */
export function useClaudiaTranscriptAutoFollow(args: {
  items: AssistantTranscriptTimelineItem[];
  narration?: AssistantTranscriptNarration | null;
  /** Scroll-back's callback ref for the same viewport, composed into the returned one. */
  viewportRef?: (node: HTMLElement | null) => void;
  /**
   * Identity of the conversation on screen (the thread id). The side panel keeps one mount across
   * thread switches, so this is what restarts the "land at the end instantly" first follow.
   */
  followKey?: string | null;
}): ClaudiaTranscriptAutoFollow {
  const {
    contentRef,
    scrollRef,
    scrollToBottom,
    isAtBottom,
    state: stickState,
  } = useStickToBottom({
    // Mounting into an existing conversation must land at the end without a visible ride down
    // through the whole history. Growth *after* that keeps the default spring.
    initial: "instant",
  });

  const fingerprint = useMemo(() => {
    const baseFingerprint = getClaudiaTranscriptAutoFollowFingerprint(args.items);
    return args.narration
      ? `${baseFingerprint ?? "empty"}:narration:${args.narration.key}:${args.narration.text}`
      : baseFingerprint;
  }, [args.items, args.narration]);

  const hasFollowedRef = useRef(false);
  const followKeyRef = useRef(args.followKey);
  if (followKeyRef.current !== args.followKey) {
    followKeyRef.current = args.followKey;
    hasFollowedRef.current = false;
  }

  useEffect(() => {
    // The first follow of a conversation lands at its end no matter what: the lock may still be
    // escaped from the reader scrolling up in the previous thread of the same mount.
    const isFirstFollow = !hasFollowedRef.current;
    if (!fingerprint || (!isFirstFollow && !stickState.isAtBottom)) {
      return;
    }
    hasFollowedRef.current = true;
    void scrollToBottom({
      animation: isFirstFollow ? "instant" : undefined,
      ignoreEscapes: isFirstFollow,
      // Ride an in-flight animation of the same behaviour instead of restarting it on every
      // streamed delta, which is what makes a long response scroll smoothly rather than stutter.
      wait: true,
    });
  }, [fingerprint, scrollToBottom, stickState]);

  const externalViewportRef = args.viewportRef;
  const composedViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      externalViewportRef?.(node);
      scrollRef(node);
    },
    [externalViewportRef, scrollRef]
  );

  const composedContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      contentRef(node);
    },
    [contentRef]
  );

  const handleScrollToBottom = useCallback(() => {
    void scrollToBottom();
  }, [scrollToBottom]);

  return {
    viewportRef: composedViewportRef,
    contentRef: composedContentRef,
    isAtBottom,
    scrollToBottom: handleScrollToBottom,
  };
}
